import { useCallback, useEffect, useState } from 'react';
import type { Person, PlanSummary } from '@ambit/shared';
import { api } from '../api.ts';

/**
 * Step four: the saved plans, sorted so whatever the family actually wants
 * floats to the top. You vote as yourself — the person picker sets who that is
 * — rather than tapping on other people's behalf.
 */
export function Shortlist({
  people,
  me,
  refreshKey,
  onOpen,
  onNeedIdentity,
}: {
  people: Person[];
  me: string | null;
  refreshKey: number;
  onOpen: (planId: string) => void;
  onNeedIdentity: () => void;
}) {
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    void api
      .listPlans({ curated: false, sort: 'votes' })
      .then(setPlans)
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  useEffect(load, [load, refreshKey]);

  async function toggleVote(plan: PlanSummary) {
    if (!me) {
      onNeedIdentity();
      return;
    }
    const mine = plan.voters.some((v) => v.personId === me);
    setBusyId(plan.id);

    // Optimistic: a vote chip that lags behind the tap feels broken.
    const person = people.find((p) => p.id === me);
    setPlans((all) =>
      all.map((p) =>
        p.id !== plan.id
          ? p
          : {
              ...p,
              voteCount: p.voteCount + (mine ? -1 : 1),
              voters: mine
                ? p.voters.filter((v) => v.personId !== me)
                : [...p.voters, { personId: me, name: person?.name ?? me, color: person?.color ?? null }],
            },
      ),
    );

    try {
      if (mine) await api.unvote(plan.id, me);
      else await api.vote(plan.id, me);
    } catch {
      load(); // put the truth back
    } finally {
      setBusyId(null);
    }
  }

  async function archive(plan: PlanSummary) {
    setBusyId(plan.id);
    setPlans((all) => all.filter((p) => p.id !== plan.id));
    try {
      await api.archivePlan(plan.id);
    } catch {
      load();
    } finally {
      setBusyId(null);
    }
  }

  if (!loaded || plans.length === 0) {
    return (
      <section aria-labelledby="shortlist-heading" className="mt-12 border-t border-spruce-800 pt-6">
        <h2 id="shortlist-heading" className="display text-xs uppercase tracking-widest text-bone-500">
          The shortlist
        </h2>
        <p className="mt-2 text-sm text-bone-500">
          {loaded ? 'Nothing saved yet. Build a day and save it, then everyone votes.' : 'Loading…'}
        </p>
      </section>
    );
  }

  // Taken from the max rather than the first row: an optimistic vote updates
  // counts without re-sorting (re-ordering under someone's finger is horrible),
  // so position is stale between loads but the badge must still be truthful.
  const leader = Math.max(...plans.map((p) => p.voteCount));

  return (
    <section aria-labelledby="shortlist-heading" className="mt-12 border-t border-spruce-800 pt-6">
      <h2 id="shortlist-heading" className="display text-xs uppercase tracking-widest text-bone-500">
        The shortlist
      </h2>
      <p className="mt-1 text-sm text-bone-500">Most votes wins.</p>

      <ul className="mt-4 space-y-3">
        {plans.map((plan) => {
          const mine = me !== null && plan.voters.some((v) => v.personId === me);
          const winning = leader > 0 && plan.voteCount === leader;

          return (
            <li
              key={plan.id}
              className={`rounded-md p-3.5 ${winning ? 'bg-spruce-700' : 'bg-spruce-800'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  onClick={() => onOpen(plan.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="display text-lg leading-tight text-bone-100">{plan.title}</span>
                  <span className="mt-0.5 block text-xs text-bone-500">
                    {plan.stopNames.join(' · ')}
                  </span>
                </button>
                {winning && (
                  <span className="display shrink-0 text-[10px] uppercase tracking-widest text-blaze-400">
                    leading
                  </span>
                )}
              </div>

              {/*
                The only way a plan reaches the calendar feed. Without this the
                column exists, the feed queries it, and nothing can ever set it
                — which is the shape of gap this project keeps finding.
              */}
              <Schedule
                plan={plan}
                onChanged={(updated) =>
                  setPlans((all) => all.map((p) => (p.id === updated.id ? updated : p)))
                }
              />

              <div className="numeric mt-2 flex gap-3 text-xs text-bone-500">
                <span>{plan.totalCost === 0 ? 'free' : `$${plan.totalCost}`}</span>
                <span>{plan.maxDrive}m drive</span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={busyId === plan.id}
                  aria-pressed={mine}
                  onClick={() => void toggleVote(plan)}
                  className={`rounded-full px-3 py-1.5 text-sm transition-colors disabled:opacity-50 ${
                    mine
                      ? 'bg-blaze-500 font-semibold text-spruce-950'
                      : 'bg-spruce-600 text-bone-100 hover:bg-spruce-500'
                  }`}
                >
                  {mine ? '✓ Voted' : 'Vote'}
                </button>

                {/*
                  Who is in favour. The person's colour is a dot, not the text:
                  `people.color` is arbitrary user-supplied hex, so using it for
                  the label would put legibility at the mercy of whoever picked
                  it. A dark blue would land at 1.5:1. This way the name is
                  always bone-100 and the colour still carries identity.
                */}
                <ul className="flex flex-wrap gap-1">
                  {plan.voters.map((voter) => (
                    <li
                      key={voter.personId}
                      className="flex items-center gap-1.5 rounded-full bg-spruce-950/50 px-2.5 py-1 text-xs text-bone-100"
                    >
                      <span
                        aria-hidden="true"
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: voter.color ?? '#35785f' }}
                      />
                      {voter.name}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  disabled={busyId === plan.id}
                  onClick={() => void archive(plan)}
                  className="ml-auto text-xs text-bone-500 underline decoration-dotted underline-offset-4 transition-colors hover:text-blaze-300 disabled:opacity-50"
                >
                  remove
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * When this day is happening, if it is.
 *
 * A date is what moves a plan from the shortlist into the calendar feed, and
 * clearing it moves it back — the shortlist is mostly candidates, and being
 * able to un-commit matters as much as committing.
 *
 * A native date input rather than a picker: this is a phone, and the OS one is
 * better than anything worth building here.
 */
function Schedule({
  plan,
  onChanged,
}: {
  plan: PlanSummary;
  onChanged: (plan: PlanSummary) => void;
}) {
  const [busy, setBusy] = useState(false);

  const set = (value: string | null) => {
    setBusy(true);
    api
      .schedulePlan(plan.id, value)
      .then(onChanged)
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <label className="display text-[10px] uppercase tracking-widest text-bone-500">
        When
        <input
          type="date"
          value={plan.scheduledOn ?? ''}
          disabled={busy}
          onChange={(e) => set(e.target.value || null)}
          className="numeric ml-2 rounded-md bg-spruce-950 px-2 py-1 text-xs normal-case tracking-normal text-bone-100 disabled:opacity-50"
        />
      </label>
      {plan.scheduledOn && (
        <button
          type="button"
          onClick={() => set(null)}
          disabled={busy}
          className="text-xs text-bone-500 underline decoration-dotted underline-offset-4 hover:text-blaze-300 disabled:opacity-50"
        >
          not yet
        </button>
      )}
    </div>
  );
}
